import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Grid,
  Card,
  CardMedia,
  Button,
  LinearProgress,
  Box,
  useTheme,
  Tooltip,
  DialogTitle,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { Close, CloudDownload } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IconButton } from "@mui/material";
import Lightbox from "yet-another-react-lightbox";
import { AttachmentApi } from "./api/attachmentApi";
import { SegmentAttachment } from "components/QueueBoard/models/workOrderTypes";
import {
  accordionStyle,
  accordionSummaryStyle,
  accordionTitleStyle,
  counterChipStyle,
} from "../workOrderSegmentDetailsDialogStyles";
import { useSnackbar } from "notistack";
import { getLocalTime } from "components/common/Utils/commonUtilityFunctions";
import { User } from "components/authentication/models/authTypes";
import localStorageHelper from "../../../../../app/localStorage";
import { useLoading } from "components/common/context/LoadingContext";
import packageJson from "../../../../../../package.json";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { ensureEditableWorkOrder } from "../../../utils/finalizationHelpers";
interface SegmentAttachedImagesProps {
  attachments: SegmentAttachment[];
  segmentIdentity: number;
  isReadOnly?: boolean;
  invoiceDocumentIdentity?: number;
}

const SegmentAttachments: React.FC<SegmentAttachedImagesProps> = ({
  attachments,
  segmentIdentity,
  isReadOnly = false,
  invoiceDocumentIdentity,
}) => {
  const theme = useTheme();
  const [openImg, setOpenImg] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadedCount, setUploadedCount] = useState<number>(0);
  const [uploadTotal, setUploadTotal] = useState<number>(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [attachmentCollection, setAttachmentCollection] = useState(attachments);

  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user")
  );
  const { showLoading, hideLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();
  const [slides, setSlides] = useState<{ src: string }[]>([]);
  const verifyEditable = async () =>
    ensureEditableWorkOrder(invoiceDocumentIdentity, enqueueSnackbar);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    if (!(await verifyEditable())) {
      return;
    }
    const files = e.target.files;

    if (!files) return;

    const validFiles = Array.from(files);
    const oversizedFile = validFiles.find(
      (file) => file.size > 15 * 1024 * 1024
    );
    if (oversizedFile) {
      enqueueSnackbar(`File "${oversizedFile.name}" exceeds the 15MB limit.`, {
        variant: "error",
      });
      return;
    }

    // Check for long MIME type
    const invalidTypeFile = validFiles.find((file) => file.type.length > 49);
    if (invalidTypeFile) {
      enqueueSnackbar(
        `File "${invalidTypeFile.name}" has an invalid MIME type length.`,
        {
          variant: "error",
        }
      );
      return;
    }

    setUploadTotal(validFiles.length);
    setUploadedCount(0);
    setUploadProgress(0);

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const formData = new FormData();
      formData.append("file", file);
      formData.append("segmentIdentity", segmentIdentity.toString());
      formData.append("file", file);
      formData.append("DisplayText", file.name); // or any user-entered text
      formData.append("OrigFileName", file.name);
      formData.append("Extension", file.name.split(".").pop() || "");
      formData.append("StreamLength", file.size.toString());
      formData.append("MIME", file.type);
      formData.append("Source", "inv.segment");
      formData.append("SourceId", segmentIdentity.toString());
      formData.append("IsActive", "true");
      formData.append("ModBy", user.userName);
      formData.append("EntBy", user.userName);
      formData.append("ModDate", getLocalTime());
      formData.append("EntDate", getLocalTime());
      try {
        const result = await AttachmentApi.uploadAttachment(
          formData,
          (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        );

        setAttachmentCollection((prev) => [
          ...prev,
          {
            attachmentIdentity: result.data.id,
            segmentIdentity: segmentIdentity,
            fileName: file.name,
            mime: file.type,
          },
        ]);

        // After each upload success
        setUploadedCount((prev) => prev + 1);
        //
      } catch (error) {
        console.error("Upload failed", error);
        enqueueSnackbar(`Uploading failed, please retry again later.`, {
          variant: "error",
        });
      } finally {
        setUploadTotal(0);
      }
    }

    setSnackbarOpen(true);

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleFilesUpload",
      {
        elapsed_ms: "124",
        title: "Segment Files Attachment",
        componentName: "AttachFiles.tsx",
        version: packageJson.version,
      }
    );
  };

  const handleRemoveImage = async (attachmentIdentity: number) => {
    if (isReadOnly) return;
    if (!(await verifyEditable())) {
      return;
    }
    const updatedImages = attachmentCollection.filter(
      (img) => img.attachmentIdentity !== attachmentIdentity
    );
    setAttachmentCollection(updatedImages);

    try {
      await AttachmentApi.removeAttachement(attachmentIdentity);
      enqueueSnackbar(`Attachment removed successfully`, {
        variant: "success",
      });
    } catch (err) {
      //console.error("Error fetching image", err);
      enqueueSnackbar(`Attachment removal failed, please retry again later.`, {
        variant: "error",
      });
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleRemoveImage",
      {
        elapsed_ms: "124",
        title: "Segment Files Attachment",
        componentName: "AttachFiles.tsx",
        version: packageJson.version,
      }
    );
  };
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadAttachment = async (
    attachmentIdentity: number,
    originalFileName: string
  ) => {
    try {
      showLoading();
      setIsDownloading(true);
      const result = await AttachmentApi.downloadAttachment(attachmentIdentity);
      // Create a temporary link to trigger the download
      const link = document.createElement("a");
      link.href = result;
      link.download = originalFileName; // E.g., "myfile.pdf"
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(result);
      document.body.removeChild(link);
      hideLoading();
      setIsDownloading(false);
    } catch (err) {
      hideLoading();
      enqueueSnackbar(`Download failed, please retry again later.`, {
        variant: "error",
      });
      setIsDownloading(false);
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "DownloadAttachments",
      {
        elapsed_ms: "124",
        title: "Attach Files",
        componentName: "AttachFiles.tsx",
        version: packageJson.version,
      }
    );
  };

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [attachmentIdentityToDelete, setAttachmentIdentityToDelete] = useState<
    number | null
  >(null);

  const handleConfirmDelete = async () => {
    setConfirmDeleteOpen(false);
    if (attachmentIdentityToDelete) {
      await handleRemoveImage(attachmentIdentityToDelete);
      setAttachmentIdentityToDelete(null);
    }
  };

  const handleCancelDeleteAttachment = () => {
    setConfirmDeleteOpen(false);
    setAttachmentIdentityToDelete(null);
  };
  return (
    <>
      <Accordion
        sx={{
          ...accordionStyle,
          backgroundColor: (theme) =>
            theme.palette.mode === "light" ? "#fff" : "#181F2A",
        }}
      >
        <AccordionSummary
          sx={accordionSummaryStyle}
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography gutterBottom sx={accordionTitleStyle}>
            Attachments
            <Chip
              variant="outlined"
              label={attachmentCollection.length}
              size="small"
              sx={counterChipStyle}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "light" ? "#f5f5f5" : "#232323",
          }}
        >
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              component="label"
              size="small"
              sx={{ textTransform: "none" }}
              disabled={isReadOnly}
            >
              Upload Attachment(s)
              <input
                type="file"
                hidden
                multiple
                onChange={handleFileSelect}
                disabled={isReadOnly}
              />
            </Button>
          </Box>

          {uploadTotal > 0 && (
            <div style={{ marginTop: 10 }}>
              <Typography>
                Uploading {uploadedCount} of {uploadTotal}
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </div>
          )}

          <Grid
            container
            spacing={2}
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === "light" ? "#f5f5f5" : "#232323",
              p: "5px",
              mt: "5px",
            }}
          >
            {attachmentCollection.map((attachmentObj, index) => (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                lg={2}
                key={`${attachmentObj.attachmentIdentity}-${index}`}
              >
                <Box
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      maxWidth: 120,
                      mx: "auto",
                      borderRadius: "8px",
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={"/document_attachment.png"}
                      alt={`Attachment ${index}`}
                      sx={{
                        width: "100%",
                        height: 120,
                        objectFit: "cover",
                        backgroundColor: "white",
                      }}
                    />
                  </Card>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: "center",
                    }}
                  >
                    <Tooltip title="Download">
                      <span>
                        <IconButton
                          size="small"
                          sx={{
                            bgcolor: "success.main",
                            color: "white",
                            "&:hover": { bgcolor: "success.dark" },
                          }}
                          onClick={() =>
                            handleDownloadAttachment(
                              attachmentObj.attachmentIdentity,
                              attachmentObj.fileName
                            )
                          }
                        >
                          <CloudDownload fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={isReadOnly ? "Work order is finalized" : "Remove"}
                    >
                      <span>
                        <IconButton
                          size="small"
                          sx={{
                            bgcolor: "error.main",
                            color: "white",
                            "&:hover": { bgcolor: "error.dark" },
                          }}
                          disabled={isReadOnly}
                          onClick={() => {
                            if (isReadOnly) return;
                            setAttachmentIdentityToDelete(
                              attachmentObj.attachmentIdentity
                            );
                            setConfirmDeleteOpen(true);
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Tooltip title={attachmentObj.fileName}>
                    <Typography
                      variant="caption"
                      sx={{
                        width: "100%",
                        maxWidth: 120,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {attachmentObj.fileName}
                    </Typography>
                  </Tooltip>
                </Box>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Dialog open={confirmDeleteOpen} onClose={handleCancelDeleteAttachment}>
        <DialogTitle
          sx={(theme) => ({
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : "#181F2A",
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.contrastText
                : "#fff",
          })}
        >
          Confirm Remove Attachment
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove attachment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDeleteAttachment}
            sx={{
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[800],
            }}
            variant="outlined"
          >
            No
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              color: theme.palette.getContrastText(theme.palette.error.main),
              backgroundColor: theme.palette.error.main,
              "&:hover": {
                backgroundColor: theme.palette.error.dark,
              },
            }}
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Lightbox
        open={openImg}
        close={() => {
          setOpenImg(false);
          setSlides([]);
        }}
        slides={slides}
        index={0}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
        }}
      />
    </>
  );
};

export default SegmentAttachments;
